import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  Eye,
  Grid3x3, List,
  MoreVertical,
  Play,
  Search,
  Star,
  Target
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

// Shared Components
import SimpleSelect from '@/components/ui/SimpleSelect';

// Services
// import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/api';
import { reviewService } from '@/services/review.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_COURSES_PERMISSION } from '@/constants/constant-permissions';

// Types
interface CourseWithProgress {
  // Course data
  course_id: string;
  course_title: string;
  description: string;
  difficulty: string;
  stage: string;
  source_type?: string; // 'scorm_imported' for SCORM courses
  banner_image_url?: string;
  thumbnail_image_url?: string;
  estimated_duration: {
    total: any;
    hours: number;
    minutes: number;
    total_minutes: number;
  };
  rating?: number;
  total_reviews?: number;
  review_count?: number;
  assignment_id?: string;
  createdAt: string;
  updatedAt: string;

  // Progress data
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  progress_percentage: number;
  is_mandatory: boolean;
  deadline_date?: string;
  last_accessed_at?: string;
  total_time_spent_seconds: number;
  completed_modules: number;
  total_modules: number;
  certificate_number?: string;
}

interface DashboardStats {
  total_courses: number;
  completed_courses: number;
  in_progress_courses: number;
  mandatory_courses: number;
  total_time_spent_hours: number;
  average_progress: number;
}

// A course is SCORM if the backend tagged it source_type='scorm_imported', or — for
// older imports that lack that field — if any tracked lesson carries SCORM runtime
// metadata (metadata.scorm), which only SCORM courses ever produce.
const isScormCourse = (course: CourseWithProgress): boolean => {
  if (course.source_type === 'scorm_imported') return true;
  const modules = (course as any).module_progress || [];
  return modules.some((m: any) =>
    (m?.lesson_progress || []).some((l: any) => l?.metadata?.scorm)
  );
};

// Course image with a BookOpen placeholder — courses with no uploaded image (SCORM imports,
// manual courses) and images that fail to load both fall back to the icon.
const CourseThumbnail = ({
  course,
  imgClassName = 'w-full h-full object-cover',
  placeholderClassName = 'w-full h-full flex items-center justify-center',
  iconClassName = 'w-12 h-12 text-primary-400',
}: {
  course: CourseWithProgress;
  imgClassName?: string;
  placeholderClassName?: string;
  iconClassName?: string;
}) => {
  const [imgError, setImgError] = useState(false);

  const raw = (course.thumbnail_image_url || course.banner_image_url || '').trim();
  const imageUrl =
    raw && raw.toLowerCase() !== 'null' && raw.toLowerCase() !== 'undefined' ? raw : '';

  // Reset so a newly uploaded image renders without a page reload.
  useEffect(() => {
    setImgError(false);
  }, [imageUrl]);

  if (!imageUrl || imgError) {
    return (
      <div className={placeholderClassName}>
        <BookOpen className={iconClassName} />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={course.course_title}
      loading="lazy"
      onError={() => setImgError(true)}
      className={imgClassName}
    />
  );
};

// Format review count
const formatReviewCount = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

// Compact Course Card Component
const CompactCourseCard = ({
  course,
  onAction,
  onRatingClick,
  onClaimCertificate,
  canRead,
  canWrite,
  certificateNumber
}: {
  course: CourseWithProgress;
  onAction: (courseId: string, action: 'start' | 'continue' | 'preview') => void;
  onRatingClick?: (courseId: string) => void;
  onClaimCertificate?: (certificateNumber: string) => void;
  canRead: boolean;
  canWrite: boolean;
  certificateNumber?: string;
}) => {
  // A finished course is never overdue - the deadline stopped mattering once
  // the learner completed it, whichever side of it they landed on.
  const isCompleted = course.status === 'COMPLETED';
  const isOverdue = !isCompleted && course.deadline_date && new Date(course.deadline_date) < new Date();
  const daysLeft = course.deadline_date ?
    Math.ceil((new Date(course.deadline_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
  const reviewCount = course.total_reviews || course.review_count || 0;
  // SCORM courses carry no meaningful estimated duration — hide the "0h 0m" time for them only.
  const isScorm = isScormCourse(course);

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-200 overflow-hidden group flex flex-col h-full">
      {/* Thumbnail */}
      <div className="relative h-32 bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden flex-shrink-0">
        <CourseThumbnail
          course={course}
          imgClassName="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          placeholderClassName="w-full h-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
        />
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2 flex gap-1">
          {course.is_mandatory && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              Mandatory
            </span>
          )}
          {course.status === 'COMPLETED' && (
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
              <CheckCircle className="w-3 h-3 inline" />
            </span>
          )}
        </div>

        {/* Progress Bar */}
        {course.status === 'IN_PROGRESS' && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm">
            <div className="h-1 bg-white/30">
              <div 
                className="h-full bg-white transition-all duration-300"
                style={{ width: `${course.progress_percentage}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content - Flex grow to push buttons to bottom */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Title - Fixed 2 lines height */}
        <h3 className="font-medium text-sm text-gray-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors min-h-[2.5rem]">
          {course.course_title}
        </h3>
        
        {/* Description - 2 lines */}
        <p className="text-xs text-gray-600 line-clamp-2 mb-2 min-h-[2rem]">
          {course.description || 'No description available'}
        </p>
        
        {/* Meta Info */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
          {!isScorm && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
               {course.estimated_duration?.total ? `${course.estimated_duration.total.hours}h ${course.estimated_duration.total.minutes}m` : 'N/A'}
            </span>
          )}
          <span className="capitalize">{course.difficulty}</span>
        </div>

        {/* Rating - Clickable to view reviews */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRatingClick?.(course.course_id);
          }}
          className="flex items-center gap-1.5 text-xs mb-2 hover:text-yellow-600 transition-colors cursor-pointer"
          title="View reviews"
        >
          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
          <span className="font-semibold text-gray-900">{(course.rating || 0).toFixed(1)}</span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-500 hover:text-primary-600">{formatReviewCount(reviewCount)} ratings</span>
        </button>

        {/* Deadline - dropped once completed, where the green badge says it all
            and "Due in -20 days" would be the only alternative. */}
        {course.deadline_date && !isCompleted && (
          <div className={`text-xs mb-2 ${isOverdue ? 'text-red-600' : daysLeft && daysLeft <= 7 ? 'text-orange-600' : 'text-gray-500'}`}>
            {isOverdue ? (
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Overdue
              </span>
            ) : (
              <span>Due in {daysLeft} days</span>
            )}
          </div>
        )}

        {/* Progress */}
        {course.status !== 'NOT_STARTED' && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${course.progress_percentage}%` }}
              />
            </div>
            <span className="text-xs text-gray-600">{course.progress_percentage}%</span>
          </div>
        )}

        {/* Spacer to push buttons to bottom */}
        <div className="flex-grow"></div>

        {/* Action Buttons - Fixed to bottom */}
        <div className="flex flex-col gap-2 mt-auto">
          {/* Claim Certificate Button - Only for completed courses with certificate */}
          {course.status === 'COMPLETED' && certificateNumber && (
            <button
              onClick={() => onClaimCertificate?.(certificateNumber)}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 sm:py-1.5 bg-amber-50 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-xs font-medium"
            >
              <Award className="w-3.5 h-3.5" />
              Claim Certificate
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => canRead && onAction(course.course_id, 'preview')}
              disabled={!canRead}
              title={!canRead ? 'You do not have permission to view courses' : undefined}
              className={`flex-1 px-2 py-2 sm:py-1.5 border rounded-lg transition-colors text-xs font-medium ${
                canRead
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
              }`}
            >
              View
            </button>
            <button
              onClick={() => canWrite && onAction(
                course.course_id,
                course.status === 'IN_PROGRESS' ? 'continue' :
                course.status === 'COMPLETED' ? 'continue' : 'start'
              )}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission to access courses' : undefined}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 rounded-lg transition-colors text-xs font-medium ${
                canWrite
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-primary-600 text-white opacity-50 cursor-not-allowed'
              }`}
            >
              {course.status === 'IN_PROGRESS' ? 'Continue' :
               course.status === 'COMPLETED' ? 'Review' : 'Start'}
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function LearnerCourses() {
  // Permission guard
  usePermissionRouteAccess([
    LEARNER_COURSES_PERMISSION.LEARNER_COURSES_READ,
    LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE,
  ]);
  const canRead = useHasPermission([LEARNER_COURSES_PERMISSION.LEARNER_COURSES_READ, LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE]);
  const canWrite = useHasPermission([LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE]);

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'in_progress' | 'completed' | 'not_started' | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastAccessed');
  
  // Fetch courses with progress from the enhanced my-courses API
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['learner-courses'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/course-tracking/my-courses');

        if (response.data.success) {
          return response.data.data as CourseWithProgress[];
        }

        return [];
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        toast.error('Failed to load your courses');
        return [];
      }
    }
  });

  // Build course_id -> certificate_number map from coursesData (returned by my-courses API)
  const courseCertificateMap = (() => {
    const map = new Map<string, string>();
    coursesData?.forEach(course => {
      if (course.certificate_number) {
        map.set(course.course_id, course.certificate_number);
      }
    });
    return map;
  })();

  // Fetch review stats for all courses
  const { data: reviewStatsMap } = useQuery({
    queryKey: ['course-review-stats', coursesData?.map(c => c.course_id)],
    queryFn: async () => {
      if (!coursesData || coursesData.length === 0) return new Map();
      return await reviewService.getBatchCourseStats();
    },
    enabled: !!coursesData && coursesData.length > 0,
  });

  // Enrich courses with review stats
  const enrichedCourses = coursesData?.map(course => {
    const stats = reviewStatsMap?.get(course.course_id);
    return {
      ...course,
      rating: stats?.avg_rating ?? course.rating ?? 0,
      total_reviews: stats?.total_reviews ?? course.total_reviews ?? 0,
      review_count: stats?.total_reviews ?? course.review_count ?? 0,
    };
  }) || [];
  
  // Fetch dashboard stats
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['learner-dashboard-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/course-tracking/dashboard');
        
        if (response.data.success) {
          return response.data.data as DashboardStats;
        }
        
        return null;
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        return null;
      }
    }
  });
  
  // Handle course action
  const handleCourseAction = (courseId: string, action: 'preview' | 'start' | 'continue') => {
    switch (action) {
      case 'start':
      case 'continue':
        navigate(`/learn/study/${courseId}`);
        break;
      case 'preview':
        navigate(`/learn/course/${courseId}`);
        break;
      default:
        break;
    }
  };

  // Handle claim certificate - navigate to public verification page
  const handleClaimCertificate = (certificateNumber: string) => {
    navigate(`/certificate/verify/${certificateNumber}`);
  };

  // Handle rating click - navigate to course preview with reviews tab
  const handleRatingClick = (courseId: string) => {
    navigate(`/learn/course/${courseId}?tab=reviews`);
  };
  
  const courses = enrichedCourses;
  const isLoading = coursesLoading || statsLoading;

  // Calculate active courses count (in_progress + not_started)
  const activeCourses = courses.filter(c => c.status === 'IN_PROGRESS' || c.status === 'NOT_STARTED').length;

  // Filter courses based on active tab and search
  const filteredCourses = courses.filter(course => {
    const matchesSearch = searchTerm === '' || 
      course.course_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = activeTab === 'all' ||
      (activeTab === 'in_progress' && course.status === 'IN_PROGRESS') ||
      (activeTab === 'completed' && course.status === 'COMPLETED') ||
      (activeTab === 'not_started' && course.status === 'NOT_STARTED');
    
    return matchesSearch && matchesTab;
  });
  
  // Sort courses
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case 'lastAccessed':
        return new Date(b.last_accessed_at || b.updatedAt).getTime() - 
               new Date(a.last_accessed_at || a.updatedAt).getTime();
      case 'progress':
        return (b.progress_percentage || 0) - (a.progress_percentage || 0);
      case 'title':
        return a.course_title.localeCompare(b.course_title);
      case 'enrolledDate':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      default:
        return 0;
    }
  });
  
  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-4 md:p-6 text-white">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 flex items-center gap-2">
              <BookOpen className="w-6 h-6 md:w-7 md:h-7" />
              My Learning
            </h1>
            <p className="text-indigo-100 text-sm md:text-base">
              Track your learning progress and manage your courses
            </p>
          </div>
          <div className="flex gap-3">
            {/* <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Download</span>
            </button> */}
            {/* <button 
              onClick={() => navigate('/learn/history')}
              className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
            >
              <History className="w-5 h-5" />
              <span className="hidden sm:inline">Learning History</span>
            </button> */}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-xs">Active Courses</p>
            <p className="text-xl md:text-2xl font-bold">
              {activeCourses}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-xs">Completed</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.completed_courses || 0}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-xs">Total Hours</p>
            <p className="text-xl md:text-2xl font-bold">{Math.round(dashboardStats?.total_time_spent_hours || 0)}h</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-xs">Avg Progress</p>
            <p className="text-xl md:text-2xl font-bold">
              {Math.round(dashboardStats?.average_progress || 0)}%
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max md:min-w-0">
            {[
              { key: 'all', label: 'All Courses', icon: BookOpen, count: courses.length },
              { key: 'in_progress', label: 'In Progress', icon: Play, count: courses.filter(c => c.status === 'IN_PROGRESS').length },
              { key: 'completed', label: 'Completed', icon: CheckCircle, count: courses.filter(c => c.status === 'COMPLETED').length },
              { key: 'not_started', label: 'Not Started', icon: Target, count: courses.filter(c => c.status === 'NOT_STARTED').length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-6 py-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters and Search */}
        <div className="p-3 md:p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {/* Sort + view toggle share one row on mobile */}
            <div className="flex items-center gap-2">
            <div className="flex-1 md:flex-none md:w-48">
              <SimpleSelect
                value={sortBy}
                onChange={(val) => setSortBy(String(val || 'lastAccessed'))}
                options={[
                  { value: 'lastAccessed', label: 'Last Accessed' },
                  { value: 'progress', label: 'Progress' },
                  { value: 'title', label: 'Title' },
                  { value: 'enrolledDate', label: 'Enrolled Date' },
                ]}
                placeholder="Sort by"
                isSearchable={false}
              />
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid3x3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 md:p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="text-center">
                <div className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-600">Loading courses...</p>
              </div>
            </div>
          ) : sortedCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600">
                {searchTerm ? `No courses found matching "${searchTerm}"` :
                 activeTab === 'in_progress' ? "You don't have any courses in progress" :
                 activeTab === 'completed' ? "You haven't completed any courses yet" :
                 activeTab === 'not_started' ? "You haven't enrolled in any courses yet" :
                 "No courses assigned yet"}
              </p>
            </div>
          ) : (
            <>
            {/* Phones get one column, where grid and list look the same: always show the grid there. */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 ${viewMode === 'list' ? 'sm:hidden' : ''}`}>
              {sortedCourses.map((course) => (
                <CompactCourseCard
                  key={course.course_id}
                  course={course}
                  onAction={handleCourseAction}
                  onRatingClick={handleRatingClick}
                  onClaimCertificate={handleClaimCertificate}
                  canRead={canRead}
                  canWrite={canWrite}
                  certificateNumber={courseCertificateMap?.get(course.course_id)}
                />
              ))}
            </div>
            {viewMode === 'list' && (
            <div className="hidden sm:block space-y-3 md:space-y-4">
              {sortedCourses.map((course) => (
                <div key={course.course_id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 hover:shadow-md transition-all">
                  <div className="flex gap-3 md:gap-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100 flex-shrink-0">
                      <CourseThumbnail course={course} iconClassName="w-8 h-8 md:w-10 md:h-10 text-primary-400" />
                    </div>
                    {/* min-w-0 so long titles/descriptions clamp instead of widening the row */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-sm md:text-base text-gray-900 line-clamp-2">{course.course_title}</h3>
                          <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{course.description}</p>
                        </div>
                        <button className="hidden md:block p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-gray-600 mb-3">
                        <span className="capitalize">{course.difficulty}</span>
                        {!isScormCourse(course) && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                           {course.estimated_duration?.total ? `${course.estimated_duration.total.hours}h ${course.estimated_duration.total.minutes}m` : 'N/A'} • {course.difficulty?.toUpperCase() || 'N/A'}
                          </span>
                        )}
                        {/* Rating - Udemy Style - Clickable */}
                        <button
                          onClick={() => handleRatingClick(course.course_id)}
                          className="flex items-center gap-1.5 hover:text-yellow-600 transition-colors cursor-pointer"
                          title="View reviews"
                        >
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-semibold text-gray-900">{(course.rating || 0).toFixed(1)}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500 hover:text-primary-600">{formatReviewCount(course.total_reviews || course.review_count || 0)} ratings</span>
                        </button>
                        {course.deadline_date && (
                          <span className="flex items-center gap-1 text-orange-600">
                            <Calendar className="w-4 h-4" />
                            Due {new Date(course.deadline_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {/* Mobile: progress on top, buttons wrap below at full width */}
                      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs md:text-sm text-gray-600">
                              {course.completed_modules}/{course.total_modules} modules • {course.progress_percentage}% complete
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                course.status === 'COMPLETED' ? 'bg-green-600' : 'bg-indigo-600'
                              }`}
                              style={{ width: `${course.progress_percentage}%` }}
                            />
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2">
                          {course.status === 'IN_PROGRESS' ? (
                            <button
                              onClick={() => canWrite && handleCourseAction(course.course_id, 'continue')}
                              disabled={!canWrite}
                              title={!canWrite ? 'You do not have permission to access courses' : undefined}
                              className={`flex-1 md:flex-none text-center whitespace-nowrap px-3 md:px-4 py-2 text-sm md:text-base rounded-lg font-medium ${
                                canWrite
                                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                  : 'bg-indigo-600 text-white opacity-50 cursor-not-allowed'
                              }`}
                            >
                              Continue Learning
                            </button>
                          ) : course.status === 'COMPLETED' ? (
                            <>
                              {courseCertificateMap?.get(course.course_id) && (
                                <button
                                  onClick={() => handleClaimCertificate(courseCertificateMap.get(course.course_id)!)}
                                  className="flex-1 md:flex-none justify-center whitespace-nowrap px-3 md:px-4 py-2 text-sm md:text-base rounded-lg font-medium flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100"
                                >
                                  <Award className="w-4 h-4" />
                                  Claim Certificate
                                </button>
                              )}
                              <button
                                onClick={() => canWrite && handleCourseAction(course.course_id, 'continue')}
                                disabled={!canWrite}
                                title={!canWrite ? 'You do not have permission to access courses' : undefined}
                                className={`flex-1 md:flex-none text-center whitespace-nowrap px-3 md:px-4 py-2 text-sm md:text-base rounded-lg font-medium ${
                                  canWrite
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-green-600 text-white opacity-50 cursor-not-allowed'
                                }`}
                              >
                                Review Course
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => canWrite && handleCourseAction(course.course_id, 'start')}
                              disabled={!canWrite}
                              title={!canWrite ? 'You do not have permission to access courses' : undefined}
                              className={`flex-1 md:flex-none text-center whitespace-nowrap px-3 md:px-4 py-2 text-sm md:text-base rounded-lg font-medium ${
                                canWrite
                                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                                  : 'bg-gray-600 text-white opacity-50 cursor-not-allowed'
                              }`}
                            >
                              Start Course
                            </button>
                          )}
                          <Link
                            to={canRead ? `/learn/course/${course.course_id}` : '#'}
                            onClick={(e) => !canRead && e.preventDefault()}
                            title={!canRead ? 'You do not have permission to view courses' : undefined}
                            className={`flex-1 md:flex-none justify-center whitespace-nowrap px-3 md:px-4 py-2 text-sm md:text-base rounded-lg font-medium flex items-center gap-2 ${
                              canRead
                                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                : 'bg-white border border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <Eye className="w-4 h-4" />
                            Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}